import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useSession } from "@pontimed/auth-client";
import { useRouterState } from "@tanstack/react-router";

import { AuthModal, type AuthModalEscapeAction } from "@/components/auth/auth-modal";
import type { AuthIntent } from "@/components/auth/auth-modal-intents";
import { PendingOAuthLinkPrompt } from "@/components/auth/pending-oauth-link-prompt";
import {
  consumeProfileCompletionMarker,
  fetchAuthProfile,
  needsProfileCompletion,
  type AuthProfileSnapshot,
} from "@/components/auth/profile-completion";

interface OpenOptions {
  /** Path the user should land on after the OAuth round-trip / cookie set. */
  returnTo?: string;
  /** Why the modal is opening — drives the contextual benefit banner above the
   *  auth methods. Omit for the generic "one account for everything" copy. */
  intent?: AuthIntent;
  /** Fires when the user closes the modal WITHOUT completing auth (X button,
   *  backdrop click, escape). Does not fire on successful sign-in. Used by
   *  route guards to fall back to a public page when the user opts out. */
  onDismiss?: () => void;
  /** Block X / backdrop / ESC / drag dismissal. Use ONLY in combination with
   *  `escapeAction` — never trap the user with no way out. Defaults to true. */
  dismissible?: boolean;
  /** Renders an explicit escape button inside the modal. Required when
   *  `dismissible: false` so the user always has an obvious exit. */
  escapeAction?: AuthModalEscapeAction;
  /** Open on the email-code sign-in with this address filled in. */
  prefillEmail?: string;
}

interface AuthModalContextValue {
  /** Open the auth modal. `returnTo` defaults to current pathname + search. */
  open: (opts?: OpenOptions) => void;
  /** Close the auth modal without auth. */
  close: () => void;
  /**
   * Whether the modal is currently asking the user to SIGN IN.
   *
   * Deliberately false while the modal is showing the post-signup profile step:
   * the user is already authenticated there, and `_authenticated.tsx` uses this
   * flag to auto-close a sign-in overlay left over from a transient session
   * outage — which would otherwise tear the profile step down the instant it
   * opened.
   */
  isOpen: boolean;
}

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

/**
 * Single source for triggering the login modal. Mounts one `<AuthModal>` at
 * the app root so any page (`/find`, profile views, account pages) can
 * overlay it without relying on a `/login` route.
 *
 * The modal owns no router-state of its own — opening it just sets local
 * state. After successful sign-in (any channel) the modal performs a hard
 * `window.location.assign(returnTo)` so the new pontimed_at cookie is
 * picked up on the next page load.
 */
export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [returnTo, setReturnTo] = useState<string>("/account/user/dashboard");
  const [intent, setIntent] = useState<AuthIntent | undefined>(undefined);
  const [dismissible, setDismissible] = useState(true);
  const [escapeAction, setEscapeAction] = useState<AuthModalEscapeAction | undefined>(undefined);
  const [prefillEmail, setPrefillEmail] = useState<string | undefined>(undefined);
  /** Non-null while the modal is finishing a signup that came back from an
   *  external round-trip (OAuth consent, magic link). See the resume effect. */
  const [profileResume, setProfileResume] = useState<AuthProfileSnapshot | null>(null);
  // Stored in a ref, not state — onDismiss fires inside closeModal and we
  // don't want a re-render between "user clicks X" and "callback runs".
  // Cleared after use so a stale callback doesn't fire on a later open.
  const onDismissRef = useRef<(() => void) | null>(null);
  // Auth-success path calls onAuthSuccess BEFORE onClose. We flip this flag,
  // then closeModal sees it and skips onDismiss. Reset on next open.
  const succeededRef = useRef(false);
  // Same signal, as state — `isOpen` (read by the `_authenticated` guard) must
  // re-render when the modal stops being a sign-in prompt and becomes the
  // post-signup profile step. Kept alongside the ref rather than replacing it:
  // closeModal reads it synchronously, before React commits.
  const [succeeded, setSucceeded] = useState(false);

  const openModal = useCallback((opts?: OpenOptions) => {
    if (opts?.returnTo) {
      setReturnTo(opts.returnTo);
    } else if (typeof window !== "undefined") {
      const path = window.location.pathname + window.location.search;
      // Avoid storing routes that were stripped (legacy /login etc.) as the
      // post-login target — fall back to dashboard.
      setReturnTo(path && path !== "/" ? path : "/account/user/dashboard");
    }
    setIntent(opts?.intent);
    setDismissible(opts?.dismissible ?? true);
    setEscapeAction(opts?.escapeAction);
    setPrefillEmail(opts?.prefillEmail);
    setProfileResume(null);
    onDismissRef.current = opts?.onDismiss ?? null;
    succeededRef.current = false;
    setSucceeded(false);
    setOpen(true);
  }, []);

  /**
   * Re-open the modal straight on the profile step after an external auth
   * round-trip. The user IS signed in here — only the `Profile` row is
   * missing — so there is no `onDismiss` to fire and no blocking gate: closing
   * is a plain "skip setup".
   */
  const openProfileCompletion = useCallback((snapshot: AuthProfileSnapshot) => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname + window.location.search;
      // `/auth/callback` + `/auth/verify` are transient landing pages that
      // redirect themselves — never keep one as the post-completion target.
      const transient = path.startsWith("/auth/");
      setReturnTo(!transient && path && path !== "/" ? path : "/account/user/dashboard");
    }
    setIntent(undefined);
    setDismissible(true);
    setEscapeAction(undefined);
    setPrefillEmail(undefined);
    setProfileResume(snapshot);
    onDismissRef.current = null;
    succeededRef.current = true;
    setSucceeded(true);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    if (!succeededRef.current) {
      onDismissRef.current?.();
    }
    onDismissRef.current = null;
    succeededRef.current = false;
    setSucceeded(false);
    setProfileResume(null);
  }, []);

  const handleAuthSuccess = useCallback(() => {
    succeededRef.current = true;
    setSucceeded(true);
  }, []);

  // Auto-trigger from URL query: any consumer (route guard, share link, link
  // from web marketing site) can append `?auth=open&next=<path>` to request
  // the modal. Read `searchStr` (raw query string) instead of the validated
  // `search` object — TanStack's per-route `validateSearch` strips unknown
  // keys, so `auth`/`next` only survive on the raw form. After firing, strip
  // the params via history.replaceState so a refresh doesn't reopen.
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  useEffect(() => {
    if (typeof searchStr !== "string" || !searchStr) return;
    const raw = searchStr.startsWith("?") ? searchStr.slice(1) : searchStr;
    const params = new URLSearchParams(raw);
    if (params.get("auth") !== "open") return;
    const next = params.get("next") ?? undefined;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the URL (`?auth=open`) is the external system this syncs from.
    openModal(next ? { returnTo: next } : undefined);
    if (typeof window !== "undefined") {
      params.delete("auth");
      params.delete("next");
      const stripped = params.toString();
      const newUrl =
        window.location.pathname + (stripped ? `?${stripped}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, [searchStr, openModal]);

  /**
   * Resume profile completion after an external auth round-trip.
   *
   * OAuth and magic-link sign-ins leave the SPA entirely, so the modal that
   * started them is long gone when the user returns — and every apps/api
   * sign-in path creates a `User` WITHOUT a `Profile`. Without a Profile the
   * user cannot book, join a waitlist or add family members.
   *
   * Same mechanism as the legacy modal (sessionStorage intent picked up by the
   * provider on return), with a `localStorage` marker so a magic link opened in
   * a new tab still resumes. The marker is consumed on read, so this fires at
   * most once per round-trip — no prompt loop, and returning users with a
   * profile are filtered out by `needsProfileCompletion`.
   */
  const { status: sessionStatus } = useSession();
  const resumeCheckedRef = useRef(false);

  useEffect(() => {
    if (sessionStatus !== "authenticated" || resumeCheckedRef.current) return;
    if (!consumeProfileCompletionMarker()) return;
    resumeCheckedRef.current = true;
    // The provider lives for the whole app session — no unmount race to guard,
    // and no cancellation flag (which a StrictMode double-effect would trip).
    void fetchAuthProfile().then((snapshot) => {
      if (snapshot && needsProfileCompletion(snapshot)) openProfileCompletion(snapshot);
    });
  }, [sessionStatus, openProfileCompletion]);

  const value = useMemo<AuthModalContextValue>(
    // `isOpen` reports sign-in only — see the interface note: once auth has
    // succeeded the modal may still be up on the profile step, and that step
    // must survive the `_authenticated.tsx` "stale auth overlay" auto-close.
    () => ({ open: openModal, close: closeModal, isOpen: open && !succeeded }),
    [openModal, closeModal, open, succeeded],
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal
        open={open}
        onClose={closeModal}
        onAuthSuccess={handleAuthSuccess}
        returnTo={returnTo}
        intent={intent}
        dismissible={dismissible}
        escapeAction={escapeAction}
        profileResume={profileResume}
        initialEmail={prefillEmail}
      />
      {/* Facebook pending link: once a session exists — after the email-code
          sign-in in this modal or a magic link in another tab — asks before
          connecting it (components/auth/pending-oauth-link.ts). */}
      <PendingOAuthLinkPrompt />
    </AuthModalContext.Provider>
  );
}

/**
 * Trigger the auth modal from anywhere inside `<AuthModalProvider>`. Returns
 * a no-op fallback if called outside the provider so the build never crashes
 * on a stale import.
 */
export function useAuthModal(): AuthModalContextValue {
  const ctx = useContext(AuthModalContext);
  if (!ctx) return { open: () => {}, close: () => {}, isOpen: false };
  return ctx;
}
